import React from "react";

export const adminInput =
  "h-10 w-full rounded-xl border border-[#3e2723]/12 bg-[#faf8f8] px-3 text-sm text-[#3e2723] outline-none transition placeholder:text-[#3e2723]/35 focus:border-[#3e2723]/25 focus:bg-white focus:ring-2 focus:ring-[#3e2723]/10";

export const adminTextarea =
  "w-full rounded-xl border border-[#3e2723]/12 bg-[#faf8f8] px-3 py-2.5 text-sm leading-relaxed text-[#3e2723] outline-none transition placeholder:text-[#3e2723]/35 focus:border-[#3e2723]/25 focus:bg-white focus:ring-2 focus:ring-[#3e2723]/10";

export const adminSelect =
  "h-11 w-full rounded-xl border border-[#3e2723]/12 bg-[#faf8f8] px-3 text-sm font-medium text-[#3e2723] outline-none focus:border-[#3e2723]/25 focus:bg-white focus:ring-2 focus:ring-[#3e2723]/10";

export const adminLabel = "mb-1.5 block text-xs font-medium text-[#3e2723]/65";

export const adminEyebrow = "text-[10px] font-bold uppercase tracking-[0.18em] text-[#3e2723]/45";

export const adminBtnSecondary =
  "inline-flex h-9 items-center justify-center gap-1.5 rounded-full border border-[#3e2723]/12 bg-white px-4 text-xs font-semibold text-[#3e2723]/75 transition hover:border-[#3e2723]/20 hover:bg-[#faf8f8]";

export const adminBtnPrimary =
  "inline-flex h-10 items-center justify-center gap-2 rounded-full bg-[#3e2723] px-6 text-sm font-semibold text-[#fbd9e5] shadow-sm transition hover:bg-[#2a1a17] disabled:cursor-not-allowed disabled:opacity-50";

export const adminBtnUpload =
  "inline-flex shrink-0 cursor-pointer items-center justify-center rounded-xl border border-[#3e2723]/12 bg-white px-3 py-2 text-[11px] font-semibold text-[#3e2723] transition hover:bg-[#fbd9e5]/35";

export const adminRange =
  "h-2 w-full cursor-pointer appearance-none rounded-full bg-[#3e2723]/10 accent-[#3e2723] [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:bg-[#3e2723] [&::-webkit-slider-thumb]:shadow";

export function AdminPanel({ title, description, children, className = "" }) {
  return (
    <section
      className={`overflow-hidden rounded-2xl border border-[#3e2723]/10 bg-white shadow-[0_8px_30px_-18px_rgba(62,39,35,0.18)] ${className}`}
    >
      {(title || description) && (
        <div className="border-b border-[#3e2723]/8 bg-[#faf8f8]/70 px-5 py-4 sm:px-6">
          {title ? <h2 className="font-display text-base font-bold text-[#3e2723] sm:text-lg">{title}</h2> : null}
          {description ? <p className="mt-1.5 max-w-3xl text-xs leading-relaxed text-[#3e2723]/58 sm:text-sm">{description}</p> : null}
        </div>
      )}
      <div className="px-5 py-5 sm:px-6">{children}</div>
    </section>
  );
}

export function AdminSubPanel({ title, children, className = "" }) {
  return (
    <div className={`space-y-4 rounded-xl border border-[#3e2723]/8 bg-[#faf8f8]/60 p-4 sm:p-5 ${className}`}>
      {title ? <p className={adminEyebrow}>{title}</p> : null}
      {children}
    </div>
  );
}

export function AdminPreviewBox({ children, className = "" }) {
  return (
    <div
      className={`flex flex-wrap items-center gap-4 rounded-xl border border-[#3e2723]/8 bg-gradient-to-br from-[#faf8f8] to-[#fbd9e5]/20 p-4 ${className}`}
    >
      {children}
    </div>
  );
}

export function AdminBadge({ variant = "default", children }) {
  const styles =
    variant === "db"
      ? "bg-rose-50 text-rose-700 ring-rose-100"
      : variant === "code"
        ? "bg-[#3e2723]/6 text-[#3e2723]/55 ring-[#3e2723]/8"
        : "bg-[#fbd9e5]/60 text-[#3e2723] ring-[#fbd9e5]";
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ${styles}`}>
      {children}
    </span>
  );
}

export function AdminTabBar({ tabs, activeId, onChange }) {
  return (
    <div
      className="inline-flex max-w-full flex-wrap items-center gap-1 rounded-2xl border border-[#3e2723]/10 bg-white p-1 shadow-[0_8px_30px_-18px_rgba(62,39,35,0.12)]"
      role="tablist"
    >
      {tabs.map(({ id, label, Icon }) => (
        <button
          key={id}
          type="button"
          role="tab"
          aria-selected={activeId === id}
          onClick={() => onChange(id)}
          className={`inline-flex items-center gap-2 rounded-xl px-3 py-2.5 text-xs font-semibold transition sm:px-4 sm:text-sm ${
            activeId === id
              ? "bg-[#3e2723] text-[#fbd9e5] shadow-sm"
              : "text-[#3e2723]/65 hover:bg-[#fbd9e5]/40 hover:text-[#3e2723]"
          }`}
        >
          {Icon ? <Icon className="h-4 w-4 shrink-0 opacity-90" aria-hidden /> : null}
          <span>{label}</span>
        </button>
      ))}
    </div>
  );
}

export function AdminLangToggle({ lang, onChange }) {
  return (
    <div className="inline-flex rounded-full border border-[#3e2723]/10 bg-[#faf8f8] p-0.5">
      {["ro", "en"].map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => onChange(l)}
          className={`rounded-full px-4 py-1.5 text-xs font-bold tracking-wide transition ${
            lang === l ? "bg-[#3e2723] text-[#fbd9e5]" : "text-[#3e2723]/60 hover:text-[#3e2723]"
          }`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
